import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "./ui/button";
import { COOKIE_BANNER } from "../data/site";

const STORAGE_KEY = "aiprof_cookie";

// Read consent safely — if localStorage is unavailable (cookies disabled,
// sandboxed iframe, some private-browsing modes) treat it as "no choice yet"
// so the banner still appears instead of silently failing.
const hasConsent = () => {
    try {
        return !!window.localStorage.getItem(STORAGE_KEY);
    } catch {
        return false;
    }
};

export default function CookieBanner() {
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (hasConsent()) return;
        // Small delay so it slides in after the page settles.
        const t = setTimeout(() => setShow(true), 600);
        return () => clearTimeout(t);
    }, []);

    const decide = (v) => {
        try {
            window.localStorage.setItem(STORAGE_KEY, v);
        } catch {
            /* storage unavailable — still dismiss for this session */
        }
        setShow(false);
    };

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 40 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-md z-[90] glass-card rounded-3xl p-5 shadow-2xl"
                    data-testid="cookie-banner"
                >
                    <h4 className="font-display font-bold text-base mb-1">{COOKIE_BANNER.heading}</h4>
                    <p className="text-sm text-gray-600 mb-4">
                        {COOKIE_BANNER.messagePrefix}
                        <span className="font-semibold">{COOKIE_BANNER.acceptInline}</span>{COOKIE_BANNER.messageMiddle}
                        <a href="/new/legal/terms" className="text-aiblue underline">{COOKIE_BANNER.termsLinkLabel}</a>{COOKIE_BANNER.messageConnector}
                        <a href="/new/legal/cookies" className="text-aiblue underline">{COOKIE_BANNER.cookieLinkLabel}</a>{COOKIE_BANNER.messageSuffix}
                    </p>
                    <div className="flex gap-2 flex-wrap">
                        <Button onClick={() => decide("all")} className="bg-aiblue text-white rounded-full px-5" data-testid="cookie-accept">{COOKIE_BANNER.accept}</Button>
                        <Button variant="outline" onClick={() => decide("decline")} className="rounded-full px-5" data-testid="cookie-decline">{COOKIE_BANNER.decline}</Button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
